import { Button, Container, Grid, Link, Typography } from '@mui/material';
import parse from 'html-react-parser';
import debounce from 'lodash/debounce';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { DELETE_ORDER, GET_ORDER, GET_ORDER_REPORT } from 'src/apiService/apiDeclaration';
import { ICONS } from 'src/assets/library';
import NewOrderForm from 'src/components/form/NewOrderForm';
import CustomSelectField from 'src/components/formFields/selectField';
import CustomTextField from 'src/components/formFields/textField';
import MenuPopover from 'src/components/menu/menu';
import PageHeader from 'src/components/pageHeader/PageHeader';
import CustomTable from 'src/components/table/table';
import TabTitle from 'src/components/tabTitle/tabTitle';
import { USER_ROLES } from 'src/config/enum';
import { orderStatusArrayAll, showErrorTost, showSuccessTost } from 'src/constants';
import { orderTableColumns } from 'src/constants/columnConfig';
import { useModal } from 'src/hooks/useModal';
import usePagination from 'src/hooks/usePagination';

const OrderPage = () => {
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();
  const status = location.state || (user.role === USER_ROLES.OPERATOR ? 'PAYMENT_RECEIVED' : '');
  const { setDialogData, handleModalClose } = useModal();
  const [search, setSearch] = useState('');
  const [orderStatus, setOrderStatus] = useState(status ? status : 'default');
  const [reportData, setReportData] = useState([]);
  const navigate = useNavigate();
  const customerId = user.role === USER_ROLES.CUSTOMER ? user._id : '';
  let params = { customerId: customerId, status: status, search: '' };
  const { paginationModel, setPaginationModel, loading, data, refresh, setQuerryParams } = usePagination(
    GET_ORDER,
    params
  );

  const updatedOrderTable = [...orderTableColumns];

  const getReportData = async () => {
    try {
      let { data } = await GET_ORDER_REPORT(new Date());
      const filteredData = data?.filter(
        (record) => record.orderId?.status[record.orderId.status.length - 1].status !== 'NEGOTIATION_FAILURE'
      );
      setReportData(filteredData);
    } catch (err) {
      console.log(err);
    }
  };
  const onSuccess = () => {
    handleModalClose();
    refresh();
    getReportData();
  };
  const handleNavigate = (body) => {
    navigate('/order-details', { state: body });
  };
  const handleReportNavigate = (body) => {
    navigate('/order-report', { state: body });
  };
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setQuerryParams((prev) => ({ ...prev, search: value }));
      }, 500),
    [setQuerryParams]
  );

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  const handleClearFilters = useCallback(() => {
    debouncedSetSearch.cancel();
    setSearch('');
    setOrderStatus(status || 'default');
    setQuerryParams({
      customerId: customerId,
      status: status || '',
      search: '',
    });
  }, [customerId, status, setQuerryParams, debouncedSetSearch]);

  useEffect(() => {
    const statusValue = orderStatus === 'default' ? '' : orderStatus;
    setQuerryParams((prev) => ({ ...prev, status: statusValue }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setQuerryParams, orderStatus]);

  useEffect(() => {
    getReportData();
  }, []);

  const onDelete = async (id) => {
    try {
      const resp = await DELETE_ORDER(id);
      onSuccess();
      showSuccessTost(resp.message);
    } catch (error) {
      showErrorTost(error);
    }
  };

  user.role !== USER_ROLES.OPERATOR &&
    updatedOrderTable.push(
      {
        field: 'totalAmount',
        headerName: 'common.totalAmount',
        renderCell: ({ row }) => (row.totalAmount ? row.totalAmount : '-'),
        flex: 1,
      },
      {
        field: 'name',
        headerName: 'common.name',
        flex: 1,
        renderCell: ({ row }) =>
          row?.customerId?.firstName ? parse(row.customerId.firstName + ' ' + row.customerId.lastName) : '-',
      },
      {
        field: 'phoneNumber',
        headerName: 'common.phone',
        renderCell: ({ row }) => row?.customerId?.phoneNumber ?? '-',
        flex: 1,
      }
    );
  user.role !== USER_ROLES.OPERATOR &&
    user.role !== USER_ROLES.CUSTOMER &&
    updatedOrderTable.push({
      field: 'action',
      headerName: 'Action',
      sortable: false,
      disableColumnMenu: true,
      width: 80,
      renderCell: ({ row }) => {
        return (
          <MenuPopover
            menuArray={[
              {
                icon: ICONS.delete,
                name: t('menu.deleteOrder'),
                action: () => {
                  setDialogData({
                    showModal: true,
                    description: t('common.delConfirmationOrder'),
                    onConfirm: () => onDelete(row._id),
                  });
                },
              },
            ]}
          />
        );
      },
    });
  return (
    <Container>
      <Grid container spacing={2}>
        <TabTitle title={'Orders'} />
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: {
              xs: 'flex-start',
              sm: 'center',
            },
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
          }}
        >
          <PageHeader pageTitle={t('navMenu.order')} />
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: { sm: 'row', xs: 'column' },
              alignItems: 'end',
              justifyContent: 'flex-end',
              gap: 2,
              flexWrap: 'nowrap',
              minWidth: 0,
              flex: 1,
              overflowX: 'auto',
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleClearFilters}
              sx={{ cursor: 'pointer', textDecoration: 'underline', whiteSpace: 'nowrap', flexShrink: 0, mb: 2 }}
            >
              {t('labels.clearFilters')}
            </Link>
            {user.role !== USER_ROLES.OPERATOR && (
              <CustomSelectField
                value={orderStatus}
                onChange={(e) => setOrderStatus(e.target.value)}
                optionsArray={orderStatusArrayAll}
                label={t('labels.filterOrderByStatus')}
                sx={{ width: { sm: '180px', xs: '160px' }, flexShrink: 0 }}
              />
            )}
            <CustomTextField
              value={search}
              onChange={handleSearchChange}
              label={t('labels.searchByOrderNumberOrName')}
              sx={{ width: { sm: '220px', xs: '180px' }, flexShrink: 0 }}
            />
            {user.role !== USER_ROLES.OPERATOR && (
              <Button
                variant="contained"
                onClick={() =>
                  setDialogData({
                    showModal: true,
                    title: t('customerPage.modalHeading'),
                    child: <NewOrderForm onSuccess={() => onSuccess()} />,
                  })
                }
                sx={{ flexShrink: 0 }}
              >
                <Typography>{t('customerPage.newOrder')}</Typography>
              </Button>
            )}
            {user.role !== USER_ROLES.CUSTOMER && user.role !== USER_ROLES.OPERATOR && (
              <Button
                variant="contained"
                disabled={!reportData?.length}
                onClick={() => handleReportNavigate(reportData)}
                sx={{ flexShrink: 0 }}
              >
                {t('labels.prodReport')}
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CustomTable
            loading={loading}
            isServerSidePagination
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            columns={updatedOrderTable}
            rows={data?.rows}
            rowCount={data?.rowCount}
            handleNavigate={() => handleNavigate()}
            onRowClick={handleNavigate}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default OrderPage;
